import {
  IDialogButton,
  IDialogMessageFragment
} from "../containers/Dialogs/Common/Dialogs";
import { IDialogOptions } from "../containers/Dialogs/Common/Dialogs.actions";
import { showError } from "../utils/errorHandler";

export const title = (title: string, origin?: string) => {
  if (typeof title !== "string") {
    showError("Title has to be a string", origin);
    return false;
  } else if (title.length < 1) {
    showError("Title has to be at least 1 character", origin);
    return false;
  }

  return true;
};

export const message = (
  message?: string | IDialogMessageFragment[],
  origin?: string
) => {
  const messageValidators = [
    {
      isValid: () => typeof message === "undefined"
    },
    {
      isValid: () => typeof message === "string"
    },
    {
      isValid: () => Array.isArray(message)
    }
  ];

  const validateMessage = (validator): boolean => validator.isValid();

  const messageArrayValidators = [
    {
      isInvalid: message => message.length === 0,
      message: "Array of messages can not be empty"
    },
    {
      isInvalid: message => message.some(m => typeof m !== "object"),
      message: "Array of messages can only contain objects"
    }
  ];

  const validateMessageArray = function(validator): boolean {
    const isInvalid = validator.isInvalid(this.message);
    if (isInvalid) {
      showError(validator.message, origin);
    }
    return !isInvalid;
  };

  const messageFragmentValidators = [
    {
      isInvalid: part => typeof part.text === "undefined",
      message: i => `Message ${i} text property is missing`
    },
    {
      isInvalid: part => typeof part.text !== "string",
      message: i => `Message ${i} text property has to be a string`
    },
    {
      isInvalid: part =>
        typeof part.url !== "undefined" && typeof part.url !== "string",
      message: i => `Message ${i} url property has to be a string`
    }
  ];

  const validateMessageFragment = function(validator): boolean {
    const isInvalid = validator.isInvalid(this.part);
    if (isInvalid) {
      showError(validator.message(this.i), origin);
    }
    return !isInvalid;
  };

  if (!messageValidators.some(validateMessage)) {
    showError(
      "Dialog message has to be a string or an array of objects",
      origin
    );
    return false;
  }

  if (Array.isArray(message)) {
    if (!messageArrayValidators.every(validateMessageArray, { message })) {
      return false;
    }

    return message.every((part, i) =>
      messageFragmentValidators.every(validateMessageFragment, { part, i })
    );
  }

  return true;
};

export const cover = (cover?: boolean, origin?: string) => {
  // Cover is not mandatory
  if (typeof cover === "undefined") {
    return true;
  } else if (typeof cover !== "boolean") {
    showError("Cover has to be a boolean", origin);
    return false;
  }

  return true;
};

export const centerMessage = (centerMessage?: boolean, origin?: string) => {
  // Center text is not mandatory
  if (typeof centerMessage === "undefined") {
    return true;
  } else if (typeof centerMessage !== "boolean") {
    showError("CenterMessage has to be a boolean", origin);
    return false;
  }

  return true;
};

export const buttons = (buttons?: IDialogButton[], origin?: string) => {
  const buttonValidators = [
    {
      isInvalid: button => typeof button !== "object",
      message: i => `Button ${i} has to be an object`
    },
    {
      isInvalid: button => typeof button.label === "undefined",
      message: i => `Button ${i} label property is missing`
    },
    {
      isInvalid: button => typeof button.label !== "string",
      message: i => `Button ${i} label property has to be a string`
    },
    {
      isInvalid: button => button.label.length < 1,
      message: i => `Button ${i} label property has to be at least 1 character`
    },
    {
      isInvalid: button =>
        typeof button.primary !== "undefined" &&
        typeof button.primary !== "boolean",
      message: i => `Button ${i} primary property has to be a boolean`
    },
    {
      isInvalid: button => typeof button.callback === "undefined",
      message: i => `Button ${i} callback property is missing`
    },
    {
      isInvalid: button => typeof button.callback !== "function",
      message: i => `Button ${i} callback property has to be a function`
    }
  ];

  const validateButton = function(validator): boolean {
    const isInvalid = validator.isInvalid(this.button);
    if (isInvalid) {
      showError(validator.message(this.i), origin);
    }
    return !isInvalid;
  };

  const buttonsValidators = [
    {
      isInvalid: (): boolean => !Array.isArray(buttons),
      message: "Buttons has to be an array"
    },
    {
      isInvalid: (): boolean => buttons.length > 2,
      message: "A dialog can have maximum 2 buttons"
    }
  ];

  const validateButtons = (validator): boolean => {
    const isInvalid = validator.isInvalid();
    if (isInvalid) {
      showError(validator.message, origin);
    }
    return !isInvalid;
  };

  // Buttons are not mandatory
  if (typeof buttons === "undefined" || buttons.length === 0) {
    return true;
  }

  if (!buttonsValidators.every(validateButtons)) {
    return false;
  }

  if (buttons.length) {
    const isInvalid = buttons.every(
      (button: IDialogButton, i: number) =>
        !buttonValidators.every(validateButton, { button, i })
    );
    return !isInvalid;
  }
};

export const dialogOptions = (
  dialog: { options: IDialogOptions },
  origin?: string
) => {
  let options: IDialogOptions;

  if (dialog && dialog.hasOwnProperty("options")) {
    options = dialog.options;
  }

  const optionValidators = [
    {
      isInvalid: () => typeof options === "undefined",
      message: "Dialog options are missing"
    },
    {
      isInvalid: () => typeof options !== "object",
      message: "Dialog options has to be an object"
    },
    {
      isInvalid: () => !title(options.title)
    },
    {
      isInvalid: () => !message(options.message)
    },
    {
      isInvalid: () => !buttons(options.buttons)
    },
    {
      isInvalid: () => !cover(options.cover)
    },
    {
      isInvalid: () => !centerMessage(options.centerMessage)
    }
  ];

  const validateOptions = (validator): boolean => {
    const isInvalid = validator.isInvalid();
    if (isInvalid && validator.message) {
      showError(validator.message, origin);
    }
    return !isInvalid;
  };

  return optionValidators.every(validateOptions);
};
